import React, { createRef, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Forms from "../../../../Componets/Form";
import toast from "react-hot-toast";
import Header from "../../../../Componets/Header";
import { create_debit_note, get_debit_note } from "../../../../Controllers/Purchases/DebitNotes";
import { get_purchase } from "../../../../Controllers/Purchases/Orders";
import forms from "../../../../Data/Forms/Purchases/DebitNotes/forms";
import headers from "../../../../Data/Forms/Purchases/DebitNotes/headers";
import Loadercomponent from "../../../../Componets/Loader";
import Bodycomponent from "../../../../Componets/Formcomponents/body";
import Titlecomponent from "../../../../Componets/Formcomponents/title";
import Formdata from "../../../../Componets/Formcomponents/formdata";
import Headercomponent from "../../../../Componets/Formcomponents/header";

import DebitNoteTable from "../../../../Data/Forms/Purchases/DebitNotes/table";

const DebitNotesForm = () => {
  let { id } = useParams();
  let navigate = useNavigate();

  const [loader, setLoader] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [header, setHeader] = useState(headers);
  const [values, setValues] = useState(forms);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [details, setDetails] = useState([]);
  const [totals, setTotals] = useState({
    subtotal: "0.000",
    taxamount: "0.000",
    discount: 0,
    delivery: 0,
    total: "0.000",
  });

  const focus = useRef([]);

  const findFieldIndex = (fieldName) => {
    return values[0].options.findIndex((option) => option.name === fieldName);
  };

  const handlePurchaseOrderChange = async (purchaseOrderId) => {
    if (!purchaseOrderId) {
      setSelectedPurchaseOrder(null);
      setDetails([]);
      updateSupplierField("");
      return;
    }

    try {
      const purchaseOrder = await get_purchase({ id: purchaseOrderId });

      if (purchaseOrder?.status) {
        setSelectedPurchaseOrder(purchaseOrder.data);

        updateSupplierField(purchaseOrder.data.supplier?.name);

        const debitNoteDetails = purchaseOrder.data.details
          .filter(item => item.unit_name?.toLowerCase() !== "service")
          .map(item => ({
            ...item,
            original_quantity: item.quantity,
            quantity: item.quantity,
            id: item._id,
            description: item.description?._id,
            name: item.description?.name,
            unit: item.unit,
            unit_name: item.unit_name,
            purchase_price: item.purchase_price,
            tax: item.tax,
            tax_amount: item.tax_amount,
            total: item.total,
          }));

        setDetails(debitNoteDetails);
        calculateTotals(debitNoteDetails);
      } else {
        toast.error("Failed to load purchase order details");
      }
    } catch (error) {
      toast.error("Error loading purchase order");
    }
  };

  const updateSupplierField = (supplierName) => {
    const currentValues = [...values];
    const supplierIndex = findFieldIndex("Supplier");
    if (supplierIndex !== -1) {
      currentValues[0].options[supplierIndex].form.value = supplierName;
      setValues(currentValues);
    }
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity || 0);
      const purchasePrice = parseFloat(item.purchase_price || 0);
      return sum + (quantity * purchasePrice);
    }, 0);

    const taxamount = items.reduce((sum, item) => sum + parseFloat(item.tax_amount || 0), 0);
    const total = subtotal + taxamount;

    setTotals({
      subtotal: subtotal.toFixed(3),
      taxamount: taxamount.toFixed(3),
      discount: 0,
      delivery: 0,
      total: total.toFixed(3),
    });
  };

  const handleDetailChange = (index, field, value) => {
    const updatedDetails = [...details];

    if (field === 'quantity') {
      const originalQuantity = parseFloat(updatedDetails[index].original_quantity || 0);
      if (parseFloat(value) > originalQuantity) {
        toast.error(`Cannot return more than original quantity (${originalQuantity})`);
        return;
      }
    }

    updatedDetails[index][field] = value;

    if (field === 'quantity' || field === 'purchase_price') {
      const item = updatedDetails[index];
      const quantity = parseFloat(item.quantity || 0);
      const purchasePrice = parseFloat(item.purchase_price || 0);
      const taxRate = item.tax?.rate ? parseFloat(item.tax.rate) : 0;

      const subtotal = quantity * purchasePrice;
      const taxAmount = subtotal * (taxRate / 100);
      const total = subtotal + taxAmount;

      updatedDetails[index].tax_amount = taxAmount;
      updatedDetails[index].total = total;
    }

    setDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = details.filter((_, i) => i !== index);
    setDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const handleAddItem = () => {
    if (!selectedPurchaseOrder) {
      toast.error("Please select a purchase order first");
      return;
    }

    const availableItems = selectedPurchaseOrder.details
      .filter(purchaseItem =>
        !details.some(debitItem => debitItem.id === purchaseItem._id) &&
        purchaseItem.unit_name?.toLowerCase() !== "service"
      );

    if (availableItems.length === 0) {
      toast.error("No more items available to return from this purchase order");
      return;
    }

    const newItem = {
      ...availableItems[0],
      original_quantity: availableItems[0].quantity,
      quantity: availableItems[0].quantity,
      id: availableItems[0]._id,
      description: availableItems[0].description?._id,
      name: availableItems[0].description?.name,
      unit: availableItems[0].unit,
      unit_name: availableItems[0].unit_name,
      purchase_price: availableItems[0].purchase_price,
      tax: availableItems[0].tax,
      tax_amount: availableItems[0].tax_amount,
      total: availableItems[0].total,
    };

    setDetails([...details, newItem]);
    calculateTotals([...details, newItem]);
  };

  const formdata = async (data) => {
    if (data?.status) {
      const formData = data.data;

      if (!formData.purchase_order) {
        toast.error("Please select a purchase order");
        return;
      }

      if (!formData.reason) {
        toast.error("Please enter a reason for the debit note");
        return;
      }

      const validDetails = details.filter(item => parseFloat(item.quantity || 0) > 0);

      if (validDetails.length === 0) {
        toast.error("Please add at least one item to return");
        return;
      }

      setButtonLoader(true);

      const debitNoteData = {
        purchase_order: formData.purchase_order,
        supplier: selectedPurchaseOrder?.supplier?._id,
        date: formData.date,
        reason: formData.reason,
        details: validDetails,
        subtotal: totals.subtotal,
        taxamount: totals.taxamount,
        discount: totals.discount,
        delivery: totals.delivery,
        total: totals.total,
      };

      const debitNote = id
        ? null
        : await create_debit_note(debitNoteData);

      if (debitNote?.status || debitNote?.success) {
        navigate("/app/purchases/debit-notes");
        toast.success(debitNote?.message);
      } else {
        toast.error(debitNote?.message || "Failed to create debit note");
      }

      setButtonLoader(false);
    }
  };

  const fetchData = async () => {
    if (id) {
      setLoader(true);
      const debitNote = await get_debit_note({ id });
      if (debitNote?.status) {
        const data = debitNote.data;
        const currentValues = [...values];

        const purchaseOrderIndex = findFieldIndex("Purchase Order");
        const dateIndex = findFieldIndex("Date");
        const reasonIndex = findFieldIndex("Reason");
        const supplierIndex = findFieldIndex("Supplier");

        if (purchaseOrderIndex !== -1) {
          currentValues[0].options[purchaseOrderIndex].form.value = data.purchase_order?._id;
          currentValues[0].options[purchaseOrderIndex].form.options = [
            { value: data.purchase_order?._id, label: `#PO-${data.purchase_order?.number}` }
          ];
        }

        if (dateIndex !== -1) {
          currentValues[0].options[dateIndex].form.value = data.date;
        }

        if (reasonIndex !== -1) {
          currentValues[0].options[reasonIndex].form.value = data.reason;
        }

        if (supplierIndex !== -1) {
          currentValues[0].options[supplierIndex].form.value = data.supplier?.name;
        }

        setValues(currentValues);
        setSelectedPurchaseOrder(data.purchase_order);
        setDetails(data.details || []);
        setTotals({
          subtotal: data.subtotal?.toFixed(3) || "0.000",
          taxamount: data.taxamount?.toFixed(3) || "0.000",
          discount: data.discount || 0,
          delivery: data.delivery || 0,
          total: data.total?.toFixed(3) || "0.000",
        });
      } else {
        toast.error(debitNote?.message);
      }
      setLoader(false);
    } else {
      const currentValues = [...values];
      const dateIndex = findFieldIndex("Date");
      if (dateIndex !== -1) {
        currentValues[0].options[dateIndex].form.value = new Date();
      }
      setValues(currentValues);
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const previousPurchaseOrderRef = useRef(null);

  const setFormData = (data) => {
    setValues(data);

    const purchaseOrderIndex = findFieldIndex("Purchase Order");
    if (purchaseOrderIndex !== -1) {
      const purchaseOrderField = data[0].options[purchaseOrderIndex].form;
      const purchaseOrderValue = purchaseOrderField.value;

      if (
        purchaseOrderValue &&
        previousPurchaseOrderRef.current !== purchaseOrderValue
      ) {
        previousPurchaseOrderRef.current = purchaseOrderValue;
        handlePurchaseOrderChange(purchaseOrderValue);
      }
    }
  };

  return (
    <Header headers={header} setHeaders={setHeader} formdata={formdata}>
      <Loadercomponent loader={loader}>
        <Forms
          form={values}
          setForm={setFormData}
          formdata={formdata}
          buttonLoader={buttonLoader}
        >
          <Bodycomponent>
            <Titlecomponent index={1} title={"Return Items"} />
            <Formdata>
              <Headercomponent index={0} name={"Items to Return"} required={true} />
              <DebitNoteTable
                details={details}
                setDetails={setDetails}
                onDetailChange={handleDetailChange}
                onRemoveDetail={handleRemoveDetail}
                onAddItem={handleAddItem}
                totals={totals}
                ref={focus}
                readOnly={!!id}
                selectedPurchaseOrder={selectedPurchaseOrder}
              />
            </Formdata>
          </Bodycomponent>
        </Forms>
      </Loadercomponent>
    </Header>
  );
};



export default DebitNotesForm;